import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../../components/ui/tabs";
import { Download, RefreshCcw, Users, Package, Route as RouteIcon, XCircle } from "lucide-react";
import { downloadExcel } from "../../lib/excel";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(n || 0);

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function Reports() {
  const today = todayIso();
  // Default range: first day of current month → today
  const firstOfMonth = today.slice(0, 8) + "01";

  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(today);
  const [tab, setTab] = useState("salesman");
  const [bySalesman, setBySalesman] = useState({ rows: [] });
  const [byProduct, setByProduct] = useState({ rows: [] });
  const [byRoute, setByRoute] = useState({ rows: [] });
  const [noOrders, setNoOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { date_from: from, date_to: to };
      const [s, p, r, n] = await Promise.all([
        api.get("/reports/by-salesman", { params }),
        api.get("/reports/by-product", { params }),
        api.get("/reports/by-route", { params }),
        api.get("/no-order-reports", { params }),
      ]);
      setBySalesman(s.data);
      setByProduct(p.data);
      setByRoute(r.data);
      setNoOrders(n.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
     
  }, []);

  const exportCurrent = () => {
    const summary = [
      ["From", from],
      ["To", to],
      ["Generated at", new Date().toLocaleString("en-IN")],
    ];
    downloadExcel(`GM_Kridam_Reports_${from}_to_${to}.xlsx`, [
      { name: "Summary", headers: ["Field", "Value"], rows: summary },
      {
        name: "By salesman",
        headers: ["Salesman", "Orders", "Items sold", "Unique stores", "Revenue (INR)"],
        rows: bySalesman.rows.map((r) => [
          r.salesman_name,
          r.orders,
          r.items_sold,
          r.unique_stores,
          r.revenue,
        ]),
      },
      {
        name: "By product",
        headers: ["Product", "Qty sold", "Orders", "Revenue (INR)"],
        rows: byProduct.rows.map((r) => [
          r.product_name,
          r.qty_sold,
          r.orders_count,
          r.revenue,
        ]),
      },
      {
        name: "By route",
        headers: [
          "Route",
          "Salesman",
          "Scheduled date",
          "Total stops",
          "Visited",
          "Completion %",
          "Orders",
          "Revenue (INR)",
        ],
        rows: byRoute.rows.map((r) => [
          r.route_name,
          r.salesman_name,
          r.scheduled_date,
          r.total_stops,
          r.stores_visited,
          r.completion_pct,
          r.orders,
          r.revenue,
        ]),
      },
    ]);
  };

  const presetRange = (days) => {
    const t = new Date();
    const f = new Date();
    f.setDate(t.getDate() - days + 1);
    setFrom(f.toISOString().slice(0, 10));
    setTo(t.toISOString().slice(0, 10));
  };

  return (
    <div className="space-y-5" data-testid="admin-reports-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="overline text-[color:var(--muted)]">Analytics</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
            Reports
          </h1>
          <p className="text-[color:var(--muted)] mt-1">
            Filter by date range — view performance by salesman, product, or route.
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-[color:var(--border)] bg-white p-4 ink-shadow flex flex-wrap items-end gap-3">
        <div>
          <label className="overline text-[color:var(--muted)]">From</label>
          <Input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="h-10 w-44"
            data-testid="reports-from-input"
          />
        </div>
        <div>
          <label className="overline text-[color:var(--muted)]">To</label>
          <Input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="h-10 w-44"
            data-testid="reports-to-input"
          />
        </div>
        <div className="flex gap-1.5">
          <Button variant="outline" onClick={() => presetRange(1)} className="h-10">
            Today
          </Button>
          <Button variant="outline" onClick={() => presetRange(7)} className="h-10">
            7 days
          </Button>
          <Button variant="outline" onClick={() => presetRange(30)} className="h-10">
            30 days
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setFrom(firstOfMonth);
              setTo(today);
            }}
            className="h-10"
          >
            This month
          </Button>
        </div>
        <div className="ml-auto flex gap-2">
          <Button
            onClick={load}
            disabled={loading}
            className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white h-10"
            data-testid="reports-run-button"
          >
            <RefreshCcw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />{" "}
            {loading ? "Running…" : "Run report"}
          </Button>
          <Button
            variant="outline"
            onClick={exportCurrent}
            className="h-10"
            data-testid="reports-export-button"
          >
            <Download className="h-4 w-4 mr-1" /> Export Excel
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="salesman" data-testid="tab-salesman">
            <Users className="h-4 w-4 mr-1.5" /> By salesman
          </TabsTrigger>
          <TabsTrigger value="product" data-testid="tab-product">
            <Package className="h-4 w-4 mr-1.5" /> By product
          </TabsTrigger>
          <TabsTrigger value="route" data-testid="tab-route">
            <RouteIcon className="h-4 w-4 mr-1.5" /> By route
          </TabsTrigger>
          <TabsTrigger value="no-orders" data-testid="tab-no-orders">
            <XCircle className="h-4 w-4 mr-1.5" /> No-order log
          </TabsTrigger>
        </TabsList>

        <TabsContent value="salesman">
          <Card>
            <Table
              testid="report-salesman-table"
              empty="No orders by any salesman in this range."
              headers={["Salesman", "Orders", "Items sold", "Unique stores", "Revenue"]}
              rows={bySalesman.rows.map((r) => [
                <span className="font-medium" key="n">{r.salesman_name || "—"}</span>,
                r.orders,
                r.items_sold,
                r.unique_stores,
                <b className="tabular-nums" key="r">{fmtINR(r.revenue)}</b>,
              ])}
              align={["left", "right", "right", "right", "right"]}
            />
            {bySalesman.rows.length > 0 && (
              <SummaryRow
                cells={[
                  "Total",
                  bySalesman.rows.reduce((a, b) => a + b.orders, 0),
                  bySalesman.rows.reduce((a, b) => a + (b.items_sold || 0), 0),
                  "—",
                  fmtINR(bySalesman.rows.reduce((a, b) => a + b.revenue, 0)),
                ]}
              />
            )}
          </Card>
        </TabsContent>

        <TabsContent value="product">
          <Card>
            <Table
              testid="report-product-table"
              empty="No products sold in this range."
              headers={["Product", "Qty sold", "Orders", "Revenue"]}
              rows={byProduct.rows.map((r) => [
                <span className="font-medium font-tamil" key="n">{r.product_name}</span>,
                <span className="tabular-nums" key="q">{r.qty_sold}</span>,
                r.orders_count,
                <b className="tabular-nums" key="r">{fmtINR(r.revenue)}</b>,
              ])}
              align={["left", "right", "right", "right"]}
            />
            {byProduct.rows.length > 0 && (
              <SummaryRow
                cells={[
                  "Total",
                  byProduct.rows.reduce((a, b) => a + (b.qty_sold || 0), 0),
                  byProduct.rows.reduce((a, b) => a + (b.orders_count || 0), 0),
                  fmtINR(byProduct.rows.reduce((a, b) => a + b.revenue, 0)),
                ]}
              />
            )}
          </Card>
        </TabsContent>

        <TabsContent value="route">
          <Card>
            <Table
              testid="report-route-table"
              empty="No routes match this range."
              headers={[
                "Route",
                "Salesman",
                "Scheduled",
                "Stops",
                "Visited",
                "Completion",
                "Orders",
                "Revenue",
              ]}
              rows={byRoute.rows.map((r) => [
                <span className="font-medium" key="n">{r.route_name}</span>,
                r.salesman_name || "—",
                r.scheduled_date || "—",
                r.total_stops,
                r.stores_visited,
                <CompletionBadge key="c" pct={r.completion_pct} />,
                r.orders,
                <b className="tabular-nums" key="r">{fmtINR(r.revenue)}</b>,
              ])}
              align={[
                "left",
                "left",
                "left",
                "right",
                "right",
                "left",
                "right",
                "right",
              ]}
            />
            {byRoute.rows.length > 0 && (
              <SummaryRow
                cells={[
                  "Total",
                  "—",
                  "—",
                  byRoute.rows.reduce((a, b) => a + b.total_stops, 0),
                  byRoute.rows.reduce((a, b) => a + b.stores_visited, 0),
                  "—",
                  byRoute.rows.reduce((a, b) => a + b.orders, 0),
                  fmtINR(byRoute.rows.reduce((a, b) => a + b.revenue, 0)),
                ]}
              />
            )}
          </Card>
        </TabsContent>

        <TabsContent value="no-orders">
          <Card>
            <Table
              testid="report-no-orders-table"
              empty="No 'no-order' reports recorded in this range."
              headers={["Date / time", "Salesman", "Store", "Reason", "Note"]}
              rows={noOrders.map((r) => [
                <span className="tabular-nums" key="d">
                  {new Date(r.created_at).toLocaleString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>,
                r.salesman_name || "—",
                <span className="font-medium" key="s">{r.store_name}</span>,
                <span
                  key="r"
                  className="text-xs px-2 py-0.5 rounded-full bg-[color:var(--mustard)]/20 text-[#9A742E]"
                >
                  {r.reason_label}
                </span>,
                <span className="text-xs text-[color:var(--muted)]" key="n">
                  {r.note || "—"}
                </span>,
              ])}
              align={["left", "left", "left", "left", "left"]}
            />
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Card({ children }) {
  return (
    <div className="rounded-xl border border-[color:var(--border)] bg-white ink-shadow overflow-hidden mt-4">
      {children}
    </div>
  );
}

function Table({ headers, rows, align, testid, empty }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid={testid}>
        <thead className="bg-[color:var(--surface-2)]">
          <tr className="text-[color:var(--muted)] uppercase text-xs">
            {headers.map((h, i) => (
              <th
                key={i}
                className={`px-4 py-3 ${align[i] === "right" ? "text-right" : "text-left"}`}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-t border-[color:var(--border)]">
              {row.map((cell, j) => (
                <td
                  key={j}
                  className={`px-4 py-3 ${align[j] === "right" ? "text-right" : "text-left"}`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td
                colSpan={headers.length}
                className="py-12 text-center text-[color:var(--muted)]"
              >
                {empty}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function SummaryRow({ cells }) {
  return (
    <div className="border-t border-[color:var(--border)] bg-[color:var(--surface-2)] px-4 py-3 grid grid-flow-col auto-cols-fr text-sm">
      {cells.map((c, i) => (
        <div
          key={i}
          className={`${i === 0 ? "font-display font-bold uppercase tracking-wide text-xs" : i === cells.length - 1 ? "text-right font-bold tabular-nums" : "text-right tabular-nums"}`}
        >
          {c}
        </div>
      ))}
    </div>
  );
}

function CompletionBadge({ pct }) {
  const tone =
    pct >= 80
      ? "bg-[color:var(--moss)]/15 text-[color:var(--moss)]"
      : pct >= 40
        ? "bg-[color:var(--mustard)]/20 text-[#9A742E]"
        : "bg-[color:var(--primary)]/10 text-[color:var(--primary)]";
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full ${tone} tabular-nums`}>
      {pct}%
    </span>
  );
}
